import { RandomTeamFormationMode } from '@/services/utils/enums';
import { shuffleArray } from '@/services/utils/globalTools';
import { translate } from '@/i18n';
import { findBestMatchingByPenalty } from '@/services/utils/matching';
import { planRandomTeamBoards, planRandomTeamCommonColors } from './TeamMatchPairingService';
import { comparePairingCriteria, matchByCriteria } from './pairingCriteria';

function getStandingValue(standingsByPlayerId, playerId, field, fallback = 0) {
  const value = standingsByPlayerId.get(playerId)?.[field];
  return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
}

function buildPlayerStats(players = [], playerStandings = []) {
  const standingsByPlayerId = new Map(playerStandings.map((row) => [row.id, row]));

  return new Map(players.map((player) => [
    player.id,
    {
      points: getStandingValue(standingsByPlayerId, player.id, 'points', 0),
      coefficient: getStandingValue(standingsByPlayerId, player.id, 'coefficient', 0),
      rating: getStandingValue(standingsByPlayerId, player.id, 'startRating', 1500),
    },
  ]));
}

function addPairCount(map, id1, id2) {
  if (!id1 || !id2 || id1 === id2) return;
  const key = [id1, id2].sort().join('|');
  map.set(key, (map.get(key) || 0) + 1);
}

function getPairCount(map, id1, id2) {
  if (!id1 || !id2 || id1 === id2) return 0;
  return map.get([id1, id2].sort().join('|')) || 0;
}

function addByeCount(map, playerId) {
  if (!playerId) return;
  map.set(playerId, (map.get(playerId) || 0) + 1);
}

function addGameCount(map, playerId) {
  if (!playerId) return;
  map.set(playerId, (map.get(playerId) || 0) + 1);
}

export function buildRandomTeamHistory(teamMatches = [], { useBoardOpponents = true } = {}) {
  const teammateCounts = new Map();
  const opponentCounts = new Map();
  const byeCounts = new Map();
  const gameCounts = new Map();

  teamMatches.forEach((teamMatch) => {
    const side1Ids = (teamMatch?.side1?.members || []).map((player) => player?.id).filter(Boolean);
    const side2Ids = (teamMatch?.side2?.members || []).map((player) => player?.id).filter(Boolean);

    for (let i = 0; i < side1Ids.length; i++) {
      for (let j = i + 1; j < side1Ids.length; j++) addPairCount(teammateCounts, side1Ids[i], side1Ids[j]);
    }
    for (let i = 0; i < side2Ids.length; i++) {
      for (let j = i + 1; j < side2Ids.length; j++) addPairCount(teammateCounts, side2Ids[i], side2Ids[j]);
    }

    if (useBoardOpponents) {
      (teamMatch?.games || []).forEach((game) => {
        if (game?.isBye) {
          [...(game.whitePlayerIds || []), ...(game.blackPlayerIds || [])]
            .forEach((playerId) => addByeCount(byeCounts, playerId));
          return;
        }

        const whiteId = game?.getSingleWhitePlayerId?.() ?? null;
        const blackId = game?.getSingleBlackPlayerId?.() ?? null;
        addGameCount(gameCounts, whiteId);
        addGameCount(gameCounts, blackId);
        addPairCount(opponentCounts, whiteId, blackId);
      });
      return;
    }

    side1Ids.forEach((side1Id) => {
      side2Ids.forEach((side2Id) => addPairCount(opponentCounts, side1Id, side2Id));
    });
    for (const id of [...side1Ids, ...side2Ids]) {
      if (!side1Ids.length || !side2Ids.length) addByeCount(byeCounts, id);
      else addGameCount(gameCounts, id);
    }
  });

  return {
    teammateCounts,
    opponentCounts,
    byeCounts,
    gameCounts,
  };
}

function buildTeamName(players = []) {
  return players.map((player) => player?.name).filter(Boolean).join('/') || translate('gameAccordion.team');
}

function lineupKey(playerIds) {
  return [...playerIds].sort().join('|');
}

function scorePlayerForTeam(player, teamPlayers, history) {
  return teamPlayers.reduce((score, teammate) =>
    score + getPairCount(history.teammateCounts, player?.id, teammate?.id) ** 2,
  0);
}

function buildRandomTeams(players = [], {
  teamSize = 2,
  round = 1,
  cycle = null,
  history = buildRandomTeamHistory([]),
  forbiddenLineups = new Set(),
} = {}) {
  const shuffled = [...players];
  shuffleArray(shuffled);
  if (teamSize === 2) {
    const byId = new Map(shuffled.map(player => [player.id, player]));
    return findBestMatchingByPenalty(shuffled, (a, b) =>
      getPairCount(history.teammateCounts, a.id, b.id) ** 2,
      (a, b) => !forbiddenLineups.has(lineupKey([a.id, b.id])),
    ).map(([a, b], index) => {
      const members = [byId.get(a), byId.get(b)];
      return { key: `R${round}-T${index + 1}`, name: buildTeamName(members), players: members, round, cycle };
    });
  }
  const teamsCount = Math.ceil(shuffled.length / teamSize);
  const teams = Array.from({ length: teamsCount }, (_, index) => ({
    key: `R${round}-T${index + 1}`,
    name: '',
    players: [],
    round,
    cycle,
  }));

  shuffled.forEach((player) => {
    const candidates = teams
      .filter((team) => team.players.length < teamSize)
      .map((team) => ({
        team,
        score: scorePlayerForTeam(player, team.players, history),
      }))
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        if (a.team.players.length !== b.team.players.length) return a.team.players.length - b.team.players.length;
        return a.team.key.localeCompare(b.team.key);
      });

    candidates[0]?.team.players.push(player);
  });

  return teams
    .filter((team) => team.players.length > 0)
    .map((team) => ({
      ...team,
      name: buildTeamName(team.players),
    }));
}

function getTeamStrength(team, statsByPlayerId) {
  return team.players.reduce((sum, player) => sum + (statsByPlayerId.get(player.id)?.points || 0), 0);
}

function getPlayerByeCount(history, playerId) {
  return history?.byeCounts?.get?.(playerId) || 0;
}

function getPlayerGameCount(history, playerId) {
  return history?.gameCounts?.get?.(playerId) || 0;
}

function calculateByeCount(playersCount, teamSize) {
  const blockSize = Math.max(1, teamSize) * 2;
  return playersCount % blockSize;
}

function splitByeAndPlayingPlayers(players = [], {
  teamSize = 2,
  history = buildRandomTeamHistory([]),
  statsByPlayerId = new Map(),
} = {}) {
  const byeCount = calculateByeCount(players.length, teamSize);
  if (byeCount <= 0) {
    return {
      byePlayers: [],
      playingPlayers: [...players],
    };
  }

  const withRandomTieBreak = [...players].map((player, index) => ({
    player,
    index,
    randomTieBreak: Math.random(),
  }));

  const byePlayers = withRandomTieBreak
    .sort((a, b) =>
      getPlayerByeCount(history, a.player?.id) - getPlayerByeCount(history, b.player?.id) ||
      getPlayerGameCount(history, b.player?.id) - getPlayerGameCount(history, a.player?.id) ||
      (statsByPlayerId.get(a.player?.id)?.points || 0) - (statsByPlayerId.get(b.player?.id)?.points || 0) ||
      (statsByPlayerId.get(a.player?.id)?.coefficient || 0) - (statsByPlayerId.get(b.player?.id)?.coefficient || 0) ||
      (statsByPlayerId.get(a.player?.id)?.rating || 0) - (statsByPlayerId.get(b.player?.id)?.rating || 0) ||
      a.randomTieBreak - b.randomTieBreak ||
      a.index - b.index
    )
    .slice(0, byeCount)
    .map(({ player }) => player);

  const byeIds = new Set(byePlayers.map((player) => player?.id).filter(Boolean));

  return {
    byePlayers,
    playingPlayers: players.filter((player) => !byeIds.has(player?.id)),
  };
}

function scoreTeamPair(team1, team2, history) {
  let score = 0;

  team1.players.forEach((player1) => {
    team2.players.forEach((player2) => {
      score += getPairCount(history.opponentCounts, player1?.id, player2?.id) ** 2;
    });
  });

  return score;
}

function pairRandomTeams(teams = [], {
  history, statsByPlayerId, playerStandings, playerHistory, controlType, boardMode, commonBoard, balanceByRating,
} = {}) {
  const plans = new Map();
  const candidates = teams.map(team => ({ ...team, id: team.key }));
  const key = (a, b) => [a.key, b.key].sort().join('|');
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const side1 = candidates[i];
      const side2 = candidates[j];
      const options = { side1Team: side1, side2Team: side2, playerStandings, playerHistory, controlType, opponentCounts: history.opponentCounts };
      const plan = boardMode ? planRandomTeamBoards(options)
        : commonBoard ? planRandomTeamCommonColors(options) : { penalty: 0 };
      const repeats = boardMode ? plan.criteria[0] : scoreTeamPair(side1, side2, history);
      const colors = boardMode ? plan.criteria[1] : plan.penalty;
      const ratingSum = team => team.players.reduce((sum, player) => sum + statsByPlayerId.get(player.id).rating, 0);
      const strength = balanceByRating
        ? Math.round(Math.abs(ratingSum(side1) - ratingSum(side2)))
        : Math.round(Math.abs(getTeamStrength(side1, statsByPlayerId) - getTeamStrength(side2, statsByPlayerId)) * 1000)
          + (boardMode ? plan.criteria[2] : 0);
      const criteria = balanceByRating ? [repeats, strength, colors] : [repeats, colors, strength];
      plans.set(key(side1, side2), { side1, side2, ...plan, criteria });
    }
  }
  const matched = matchByCriteria(candidates, (a, b) => plans.get(key(a, b)).criteria);
  if (matched.length * 2 !== teams.length) throw new Error('Incomplete random team matching');
  const byId = new Map(candidates.map(team => [team.id, team]));
  return matched.map(([a, b]) => {
    const plan = plans.get(key(byId.get(a), byId.get(b)));
    const side1 = plan.swap ? plan.side2 : plan.side1;
    const side2 = plan.swap ? plan.side1 : plan.side2;
    return { side1Id: side1.key, side2Id: side2.key, side1, side2,
      boardAssignments: plan.assignments, criteria: plan.criteria, isBye: false };
  }).sort((a, b) => Math.max(getTeamStrength(b.side1, statsByPlayerId), getTeamStrength(b.side2, statsByPlayerId))
    - Math.max(getTeamStrength(a.side1, statsByPlayerId), getTeamStrength(a.side2, statsByPlayerId)))
    .map((pair, index) => ({ ...pair, board: index + 1 }));
}

function buildPlayerByePairs(byePlayers = [], { round = 1, cycle = null, startBoard = 1 } = {}) {
  if (!byePlayers.length) return [];

  const key = `R${round}-BYE`;
  const team = {
    key,
    name: buildTeamName(byePlayers),
    players: byePlayers,
    round,
    cycle,
  };

  return [{
    side1Id: key,
    side2Id: null,
    side1: team,
    side2: null,
    board: startBoard,
    isBye: true,
    isPlayerBye: true,
    byePlayers,
  }];
}

export function buildRandomTeamRoundPairings({
  players = [],
  teamSize = 2,
  round = 1,
  cycle = null,
  playerStandings = [],
  pastTeamMatches = [],
  mode = RandomTeamFormationMode.RANDOM,
  useBoardOpponents = true,
  playerHistory = new Map(),
  controlType = null,
  commonBoard = false,
  formationAttempts = 12,
  previousTeamLineups = [],
  balanceByRating = false,
} = {}) {
  const history = buildRandomTeamHistory(pastTeamMatches, { useBoardOpponents });
  const statsByPlayerId = buildPlayerStats(players, playerStandings);
  const forbiddenLineups = new Set(previousTeamLineups.map(lineupKey));

  if (mode !== RandomTeamFormationMode.RANDOM) {
    throw new Error(`Random team formation mode ${mode} is not implemented yet`);
  }

  const { byePlayers, playingPlayers } = splitByeAndPlayingPlayers(players, {
    teamSize,
    history,
    statsByPlayerId,
  });
  let teams = [];
  let pairedTeams = [];
  let bestCriteria = null;
  const evaluateTeams = candidateTeams => {
    if (candidateTeams.flatMap(team => team.players).length !== playingPlayers.length ||
      candidateTeams.some(team => forbiddenLineups.has(lineupKey(team.players.map(player => player.id))))) return false;
    const candidatePairs = pairRandomTeams(candidateTeams, {
      history, statsByPlayerId, playerStandings, playerHistory, controlType,
      boardMode: useBoardOpponents && !commonBoard, commonBoard, balanceByRating,
    });
    const teammatePenalty = candidateTeams.reduce((sum, team) => sum + team.players.reduce((subtotal, player, index) =>
      subtotal + scorePlayerForTeam(player, team.players.slice(0, index), history), 0), 0);
    const criteria = candidatePairs.reduce((total, pair) => total.map((value, index) => value + pair.criteria[index]), [teammatePenalty, 0, 0]);
    if (!bestCriteria || comparePairingCriteria(criteria, bestCriteria) < 0) {
      bestCriteria = criteria;
      teams = candidateTeams;
      pairedTeams = candidatePairs;
      return true;
    }
    return false;
  };
  for (let attempt = 0; attempt < formationAttempts; attempt++) {
    evaluateTeams(buildRandomTeams(playingPlayers, { teamSize, round, cycle, history, forbiddenLineups }));
  }
  if (!bestCriteria) throw new Error(translate('serviceMessages.playoffTeamLineupUnavailable'));
  // Если независимый подбор напарников оставил повторы, пробуем обмены
  // между командами с повторной оценкой всех встреч и расстановок тура.
  let exchanges = 0;
  let improved = true;
  while (improved && (bestCriteria[0] > 0 || (balanceByRating && bestCriteria[1] > 0)) && exchanges < 128) {
    improved = false;
    const base = teams;
    for (let i = 0; i < base.length && exchanges < 128; i++) {
      for (let j = i + 1; j < base.length && exchanges < 128; j++) {
        for (let a = 0; a < base[i].players.length && exchanges < 128; a++) {
          for (let b = 0; b < base[j].players.length && exchanges < 128; b++) {
            const candidate = base.map(team => ({ ...team, players: [...team.players] }));
            [candidate[i].players[a], candidate[j].players[b]] = [candidate[j].players[b], candidate[i].players[a]];
            candidate[i].name = buildTeamName(candidate[i].players);
            candidate[j].name = buildTeamName(candidate[j].players);
            exchanges++;
            if (evaluateTeams(candidate)) improved = true;
          }
        }
      }
    }
  }
  const byePairs = buildPlayerByePairs(byePlayers, {
    round,
    cycle,
    startBoard: pairedTeams.length + 1,
  });

  return {
    teams,
    byePlayers,
    pairs: [...pairedTeams, ...byePairs],
  };
}
